'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiRequirement } from '@shared/all/domains/factiiis';
import type { Post } from '@shared/all/domains/posts';
import type { Upload } from '@shared/all/domains/uploads';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import usePostBody from '@shared/utils/hooks/usePostBody';
import usePreference from '@shared/utils/hooks/usePreference';
import { Loader2 } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type React from 'react';
import { type RefObject, useEffect, useRef, useState } from 'react';
import useContentUIRenderer from '../../hooks/useContentUIRenderer';
import useOnScreen from '../../hooks/useOnScreen';
import { animDelay } from '../../utils/animDelay';
import { spaceConfig } from '../../utils/getSpaceConfig';
import { client } from '../../utils/trpc';
import { getDisplaySrc, getUploadSrc } from '../../utils/uploads';
import DocumentItem from '../common/DocumentItem';
import VideoPlayer from '../UI/Video';
import AnimatedTroll from './AnimatedTroll';
import DisappearingData from './DisappearingData';
import PostItemActions from './PostItemActions';
import PostItemHead from './PostItemHead';
import ViewPostMedia from './ViewPostMedia';
type Props = {
  post: Post;
  nsfw: boolean;
  politics: boolean;
  troll: boolean;
  isFullLength: boolean;
  disableLink: boolean;
  isInView: boolean;
  ignorePostFactiiiIds: number[]; // FactiiiIds to ignore when rendering attachments/postFactiiisCount
  minified?: boolean;
  // Cancels the parent card's p-4 horizontal padding so media sits edge-to-edge on mobile.
  // Only the top-level feed PostItem opts in; reply bubbles and modal embeds keep their inset.
  fullBleedMedia?: boolean;
  onContentEditClick?: () => void;
};
const PostItemBody = props => {
  const $ = _c(59);
  const videoRef = useRef(null);
  const {
    isInMiddle: videoIsInMiddle
  } = useOnScreen(videoRef);
  const {
    preferenceState
  } = usePreference(client);
  const {
    localPreferenceState
  } = useLocalPreference();
  const {
    contentUIRenderHandler
  } = useContentUIRenderer();
  const pathname = usePathname();
  const [hasInteracted, setHasInteracted] = useState(false);
  const [viewPostMedia, setViewPostMedia] = useState(null);
  const [showTrollAnimation, setShowTrollAnimation] = useState(false);
  let t0;
  let t1;
  if ($[0] !== props.isInView || $[1] !== props.troll || $[2] !== showTrollAnimation) {
    t0 = () => {
      let trollTimer;
      if (props.troll && !showTrollAnimation && props.isInView) {
        trollTimer = setTimeout(() => {
          setShowTrollAnimation(true);
        }, 3000);
      }
      return () => {
        clearTimeout(trollTimer);
      };
    };
    t1 = [props.troll, props.isInView, showTrollAnimation];
    $[0] = props.isInView;
    $[1] = props.troll;
    $[2] = showTrollAnimation;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== videoIsInMiddle) {
    t2 = () => {
      const v = videoRef.current;
      if (!v) {
        return;
      }
      if (videoIsInMiddle) {
        v.play().catch(_temp);
      } else {
        v.pause();
      }
    };
    t3 = [videoIsInMiddle];
    $[5] = videoIsInMiddle;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const handleInteraction = () => {
        setHasInteracted(true);
        window.removeEventListener("click", handleInteraction);
        window.removeEventListener("touchstart", handleInteraction);
      };
      window.addEventListener("click", handleInteraction);
      window.addEventListener("touchstart", handleInteraction);
      return () => {
        window.removeEventListener("click", handleInteraction);
        window.removeEventListener("touchstart", handleInteraction);
      };
    };
    t5 = [];
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  const body = usePostBody(client, props.post);
  const t6 = props.isFullLength ? body.content : props.post.content;
  let t7;
  if ($[10] !== contentUIRenderHandler || $[11] !== props.isFullLength || $[12] !== props.post.id || $[13] !== t6) {
    t7 = contentUIRenderHandler(t6, props.post.id, props.isFullLength);
    $[10] = contentUIRenderHandler;
    $[11] = props.isFullLength;
    $[12] = props.post.id;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const renderedContent = t7;
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[15] !== body || $[16] !== hasInteracted || $[17] !== localPreferenceState || $[18] !== pathname || $[19] !== preferenceState.allowPoliticalContent || $[20] !== props || $[21] !== renderedContent || $[22] !== showTrollAnimation || $[23] !== videoIsInMiddle) {
    const renderAttachments = uploads => {
      if (!uploads) {
        return null;
      }
      if (uploads.length === 1) {
        const x = uploads[0];
        return <div className={`anim-enter mb-4 overflow-hidden [--enter-duration:300ms] ${props.fullBleedMedia ? "-mx-4 rounded-none sm:mx-0 sm:rounded-md" : "rounded-md"}`}>{x.type === "IMAGE" && <div className="relative overflow-hidden rounded-md transition-transform duration-300 hover:scale-[1.01]"><Image width={x.display?.width ?? 800} height={x.display?.height ?? 600} sizes="(max-width: 640px) 100vw, 640px" loading={props.isFullLength ? "eager" : undefined} fetchPriority={props.isFullLength ? "high" : undefined} style={{
              width: "100%",
              height: "auto",
              maxHeight: "600px",
              objectFit: "contain",
              filter: props.nsfw ? "blur(10px)" : "none"
            }} src={getDisplaySrc(x)} alt={props.post.title?.trim() || `Image posted by @${props.post.user.username}`} onClick={e => {
              e.stopPropagation();
              setViewPostMedia({
                uploads,
                index: 0
              });
            }} className="transition-all duration-300" /></div>}{x.type === "VIDEO" && <div className="relative overflow-hidden rounded-md"><VideoPlayer ref={videoRef} src={getUploadSrc(x)} preload="metadata" style={{
              filter: props.nsfw ? "blur(10px)" : "none"
            }} autoPlay={videoIsInMiddle && !pathname?.startsWith("/post")} loop={videoIsInMiddle} playsInline={true} muted={Boolean(localPreferenceState.muteVideo) || !hasInteracted && videoIsInMiddle && !pathname?.startsWith("/post")} onClick={_temp2} /></div>}{x.type === "DOCUMENT" && <DocumentItem data={x} inline={true} />}</div>;
      } else {
        if (uploads.length > 1) {
          return <div className={`anim-enter mb-4 grid grid-cols-2 gap-3 [--enter-duration:300ms] ${props.fullBleedMedia ? "-mx-4 sm:mx-0" : ""}`}>{uploads.slice(0, 4).map((x_0, i) => <div key={x_0.id} style={animDelay(i * 100)} className="anim-enter relative overflow-hidden rounded-md [--enter-scale:0.95]">{x_0.type === "IMAGE" && <div className="transition-transform duration-300 hover:scale-[1.03]"><Image width={x_0.display?.width ?? 800} height={x_0.display?.height ?? 600} sizes="50vw" style={{
                  width: "100%",
                  height: "280px",
                  objectFit: "cover",
                  filter: props.nsfw ? "blur(10px)" : "none"
                }} src={getDisplaySrc(x_0)} alt={x_0.name ?? ""} onClick={e_1 => {
                  e_1.stopPropagation();
                  setViewPostMedia({
                    uploads,
                    index: i
                  });
                }} className="rounded-md transition-all duration-300" /></div>}{x_0.type === "VIDEO" && <div style={{
                height: "280px"
              }}><VideoPlayer ref={videoRef} src={getUploadSrc(x_0)} preload="metadata" style={{
                  filter: props.nsfw ? "blur(10px)" : "none"
                }} playsInline={true} muted={Boolean(localPreferenceState.muteVideo) || !hasInteracted && videoIsInMiddle && !pathname?.startsWith("/post")} onClick={_temp3} /></div>}{x_0.type === "DOCUMENT" && <DocumentItem data={x_0} inline={true} />}{i === 3 && uploads.length > 4 && <div className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-md bg-black/60 text-white transition-all duration-300 hover:bg-black/70"><span className="text-xl font-semibold">+{uploads.length - 4}</span></div>}</div>)}</div>;
        }
      }
    };
    t8 = `transition-all duration-200 ${props.minified ? "py-2" : "py-4"}`;
    if ($[28] !== body || $[29] !== preferenceState.allowPoliticalContent || $[30] !== props || $[31] !== renderedContent || $[32] !== showTrollAnimation) {
      t9 = !preferenceState.allowPoliticalContent && props.politics ? <div className="anim-enter rounded-md bg-background-secondary p-4 shadow-md [--enter-duration:300ms]"><p className="mb-2 text-lg font-semibold text-destructive">Content Marked Political</p><p className="text-textPrimary">Please update your settings by clicking <Link onClick={_temp4} href="/settings/preferences"><span className="text-primary underline transition-colors duration-200 hover:text-indigo-400">here</span></Link>.</p></div> : <div className="max-w-none text-textPrimary">{props.post.title && <div className={props.minified ? "mt-2" : "mt-1"}><h4 className={props.minified ? "text-[15px] font-semibold leading-snug text-textPrimary" : "text-xl font-semibold tracking-tight text-textPrimary"}>{props.post.title}</h4>{props.minified && props.post.content && <><div className="my-2 border-t border-ds-border-subtle" /><p className="line-clamp-2 text-xs leading-relaxed text-textSecondary/50">{props.post.content}</p></>}</div>}{showTrollAnimation && <AnimatedTroll setShowTrollAnimation={setShowTrollAnimation} />}{props.post.title && !props.minified && props.isFullLength && props.post.content && <div className="mt-4 border-t border-ds-border-standard pt-3"><p className="mb-1 text-xs font-emphasis uppercase tracking-wide text-textSecondary/50">Description</p></div>}<div className={`prose prose-sm sm:prose-base max-w-none text-textPrimary ${props.onContentEditClick ? "cursor-text" : ""}`} onDoubleClick={props.onContentEditClick ? e_4 => {
          e_4.stopPropagation();
          props.onContentEditClick?.();
        } : undefined}>{(!props.post.title || props.isFullLength) && renderedContent}</div>{props.isFullLength && !body.complete && <div className="mt-2 flex items-center gap-2 text-sm">{body.error && <span className="text-textSecondary">Could not load the rest of this post.</span>}{body.isLoading ? <Loader2 className="size-4 animate-spin text-primary" /> : <button type="button" onClick={body.loadMore} className="font-emphasis text-primary hover:underline">{body.error ? "Try again" : "View more"}</button>}</div>}</div>;
      $[28] = body;
      $[29] = preferenceState.allowPoliticalContent;
      $[30] = props;
      $[31] = renderedContent;
      $[32] = showTrollAnimation;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    if ($[34] !== props.disableLink || $[35] !== props.ignorePostFactiiiIds || $[36] !== props.isFullLength || $[37] !== props.isInView || $[38] !== props.minified || $[39] !== props.nsfw || $[40] !== props.politics || $[41] !== props.post.repost || $[42] !== props.troll) {
      t10 = props.post.repost && <div className="anim-enter mt-4 rounded-md border border-ds-border-subtle p-3 transition-all duration-200 [--enter-delay:100ms] [--enter-duration:300ms] [--enter-y:10px] hover:border-ds-border-hover"><RepostItem isFullLength={props.isFullLength} disableLink={props.disableLink} post={props.post.repost} nsfw={props.nsfw} politics={props.politics} troll={props.troll} isInView={props.isInView} ignorePostFactiiiIds={props.ignorePostFactiiiIds} minified={props.minified} /></div>;
      $[34] = props.disableLink;
      $[35] = props.ignorePostFactiiiIds;
      $[36] = props.isFullLength;
      $[37] = props.isInView;
      $[38] = props.minified;
      $[39] = props.nsfw;
      $[40] = props.politics;
      $[41] = props.post.repost;
      $[42] = props.troll;
      $[43] = t10;
    } else {
      t10 = $[43];
    }
    let t12;
    if ($[44] !== props.ignorePostFactiiiIds) {
      t12 = d => !props.ignorePostFactiiiIds.includes(d.id);
      $[44] = props.ignorePostFactiiiIds;
      $[45] = t12;
    } else {
      t12 = $[45];
    }
    t11 = renderAttachments([...(props.post.uploads || []), ...(props.post.postFactiiisCount || []).filter(t12).flatMap(_temp6)]);
    $[15] = body;
    $[16] = hasInteracted;
    $[17] = localPreferenceState;
    $[18] = pathname;
    $[19] = preferenceState.allowPoliticalContent;
    $[20] = props;
    $[21] = renderedContent;
    $[22] = showTrollAnimation;
    $[23] = videoIsInMiddle;
    $[24] = t10;
    $[25] = t11;
    $[26] = t8;
    $[27] = t9;
  } else {
    t10 = $[24];
    t11 = $[25];
    t8 = $[26];
    t9 = $[27];
  }
  let t12;
  if ($[46] !== props.post.id || $[47] !== props.post.postFactiiisCount) {
    t12 = spaceConfig.space.slug && props.post.postFactiiisCount?.filter(_temp7).map(factiii => <div key={factiii.factiiiId} className="anim-enter [--enter-duration:300ms]"><DisappearingData postId={props.post.id} factiii={factiii} /></div>);
    $[46] = props.post.id;
    $[47] = props.post.postFactiiisCount;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== props.nsfw || $[50] !== viewPostMedia) {
    t13 = viewPostMedia !== null && <ViewPostMedia closeHandler={() => {
      setViewPostMedia(null);
      document.body.classList.remove("removeScroller");
    }} data={viewPostMedia.uploads} initialIndex={viewPostMedia.index} nsfw={props.nsfw} />;
    $[49] = props.nsfw;
    $[50] = viewPostMedia;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== t10 || $[53] !== t11 || $[54] !== t12 || $[55] !== t13 || $[56] !== t8 || $[57] !== t9) {
    t14 = <div className={t8}>{t9}{t10}{t11}{t12}{t13}</div>;
    $[52] = t10;
    $[53] = t11;
    $[54] = t12;
    $[55] = t13;
    $[56] = t8;
    $[57] = t9;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  return t14;
};
const RepostItem: React.FC<Props> = props => {
  const $ = _c(26);
  const [nsfw, setNsfw] = useState(props.nsfw);
  const [politics, setPolitics] = useState(props.politics);
  const [troll, setTroll] = useState(props.troll);
  let t0;
  if ($[0] !== nsfw || $[1] !== politics || $[2] !== props.minified || $[3] !== props.post || $[4] !== troll) {
    t0 = <PostItemHead post={props.post} nsfw={nsfw} politics={politics} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} minified={props.minified} />;
    $[0] = nsfw;
    $[1] = politics;
    $[2] = props.minified;
    $[3] = props.post;
    $[4] = troll;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== nsfw || $[7] !== politics || $[8] !== props.disableLink || $[9] !== props.ignorePostFactiiiIds || $[10] !== props.isFullLength || $[11] !== props.isInView || $[12] !== props.minified || $[13] !== props.post || $[14] !== troll) {
    t1 = <PostItemBody post={props.post} disableLink={props.disableLink} isFullLength={props.isFullLength} nsfw={nsfw} politics={politics} troll={troll} isInView={props.isInView} ignorePostFactiiiIds={props.ignorePostFactiiiIds} minified={props.minified} />;
    $[6] = nsfw;
    $[7] = politics;
    $[8] = props.disableLink;
    $[9] = props.ignorePostFactiiiIds;
    $[10] = props.isFullLength;
    $[11] = props.isInView;
    $[12] = props.minified;
    $[13] = props.post;
    $[14] = troll;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  let t2;
  if ($[16] !== nsfw || $[17] !== politics || $[18] !== props.minified || $[19] !== props.post || $[20] !== troll) {
    t2 = <div className="border-t border-ds-border-subtle pt-2"><PostItemActions post={props.post} nsfw={nsfw} politics={politics} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} minified={props.minified} /></div>;
    $[16] = nsfw;
    $[17] = politics;
    $[18] = props.minified;
    $[19] = props.post;
    $[20] = troll;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  let t3;
  if ($[22] !== t0 || $[23] !== t1 || $[24] !== t2) {
    t3 = <div className="rounded-md transition-all duration-200">{t0}{t1}{t2}</div>;
    $[22] = t0;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
};
export default PostItemBody;
function _temp() {}
function _temp2(e_0) {
  e_0.preventDefault();
  e_0.stopPropagation();
  if (e_0.currentTarget.paused) {
    e_0.currentTarget.play();
  }
}
function _temp3(e_2) {
  e_2.preventDefault();
  e_2.stopPropagation();
  if (e_2.currentTarget.paused) {
    e_2.currentTarget.play();
  }
}
function _temp4(e_3) {
  return e_3.stopPropagation();
}
function _temp5(p) {
  return p.uploads;
}
function _temp6(d_0) {
  return d_0.postFactiiis.flatMap(_temp5) || [];
}
function _temp7(postFactiii) {
  return postFactiii?.requirements.includes(FactiiiRequirement.DISAPPEARING_DATA) && !postFactiii.postFactiiis[0]?.data;
}